import { Icon } from "@/components/Icons";

/**
 * Relationship-check result contract — mirrors the server shape returned by
 * buildSignalRelationships (supabase/functions/_shared/relationships.ts) and
 * surfaced through record_signal_action_event (internal tier) and
 * record_magic_link_recipient_action (recipient tier) as { event, result }.
 * Grouped by the owning consultant. department is currently always null from the
 * server; the contact line omits the parenthetical when it is.
 */
export interface RelationshipContact {
  contactId: string;
  name: string;
  title: string | null;
  department: string | null;
  matchBasis?:
    | "explicit_organization_id"
    | "project_id"
    | "project_id_redirect"
    | "exact_canonical_name"
    | "exact_ratified_alias"
    | "participant"
    | "excluded_project_id"
    | "excluded_internal_label"
    | "none";
}

export interface ConsultantRelationshipEdge {
  ownerConsultantId: string;
  /**
   * Display name of the owning consultant, or null when the owner can't be
   * resolved to a name (legacy / departed owner). Never the string "Unknown
   * consultant" — a null owner renders the "A colleague …" copy instead.
   */
  ownerConsultantName: string | null;
  contacts: RelationshipContact[];
}

export interface SignalRelationshipResult {
  status: "complete";
  signalId: string;
  organizationId: string | null;
  companyName: string | null;
  matchBasis: "organization_id" | "normalized_company" | "none";
  sources: string[];
  relationships: ConsultantRelationshipEdge[];
  count: number;
}

/**
 * Optional wiring for the per-owner "Pass signal to X" action. When provided
 * (internal tier) the button assigns the signal to that consultant through the
 * server-side assign_signal Edge Function, pre-targeting the owner. When omitted
 * (recipient tier — a token recipient cannot assign) the button is
 * disabled-honest. pending/passed/error owner ids drive per-owner feedback off
 * the single shared assign mutation.
 */
export interface RelationshipPassAction {
  onPass: (ownerConsultantId: string) => void;
  pendingOwnerId: string | null;
  passedOwnerId: string | null;
  errorOwnerId: string | null;
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function contactLine(c: RelationshipContact): string {
  const title = c.title ? `, ${c.title}` : "";
  const department = c.department ? ` (${c.department})` : "";
  return `${c.name}${title}${department}`;
}

/**
 * Firm relationship-check result, rendered identically in both tiers in the
 * intelligence column below the on-demand run buttons, per the archived legacy
 * reference. "Request introduction" is disabled-honest in both tiers (arrives
 * with email in a later phase). "Pass signal to X" is wired to assign_signal on
 * the internal tier (pass prop supplied) and disabled-honest on the recipient
 * tier (pass omitted — a token recipient cannot assign).
 */
export function RelationshipResults({
  result,
  fallbackCompany,
  pass,
  rosterIds,
}: {
  result: SignalRelationshipResult;
  fallbackCompany?: string;
  pass?: RelationshipPassAction;
  /**
   * Active-roster consultant ids — the same source the Assign popover uses
   * (listAssignableUsers ∪ the viewer). Supplied on the internal tier so a
   * resolved owner who is NOT an active BDSE user gets a disabled Pass with a
   * note, while a roster owner keeps the live "Pass signal to X". Omitted on the
   * recipient tier (no assign; every owner's Pass is disabled-honest anyway).
   */
  rosterIds?: ReadonlySet<string>;
}): JSX.Element {
  const company = result.companyName || fallbackCompany || "this company";

  return (
    <div className="sx-rel">
      <p className="sx-rel-head">
        Check for an existing relationship at {company}
      </p>

      {result.relationships.length === 0 ? (
        <p className="sx-rel-empty">
          {"No relationships found in the firm — this one's all yours."}
        </p>
      ) : (
        <>
          {result.relationships.map((edge, i) => {
            const name = edge.ownerConsultantName;
            const onRoster = name != null && !!rosterIds?.has(edge.ownerConsultantId);
            // Case b (internal): a resolved owner who is not an active BDSE user
            // keeps their name + contact intel, but Pass is disabled with a note.
            const offRosterNote = pass != null && name != null && !onRoster;
            return (
              <div
                className="sx-rel-group"
                key={edge.ownerConsultantId || `owner-${i}`}
              >
                <p className="sx-rel-group-title">
                  {name != null
                    ? `${name} has a relationship at ${company}:`
                    : `A colleague has a relationship at ${company}:`}
                </p>
                {edge.contacts.map((c) => (
                  <p className="sx-rel-contact" key={c.contactId}>
                    {contactLine(c)}
                  </p>
                ))}
                <div className="sx-rel-actions">
                  <PassControl
                    edge={edge}
                    name={name}
                    onRoster={onRoster}
                    pass={pass}
                  />
                  <button
                    type="button"
                    className="sx-btn sx-btn-p3"
                    disabled
                    title="Coming soon"
                  >
                    Request introduction
                  </button>
                </div>
                {offRosterNote ? (
                  <p className="sx-helper">Not an active BDSE user</p>
                ) : null}
              </div>
            );
          })}
          <div className="sx-gate-note">
            <Icon name="clock" />
            <p className="sx-helper">
              {pass
                ? "Introductions arrive with email in a later phase."
                : "Passing a signal and requesting an introduction aren't available on a shared link."}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Per-owner Pass control. Live assign only in case (a): the internal tier AND a
 * resolved owner on the active roster. Case (b) resolved-but-off-roster, case (c)
 * null owner, and the whole recipient tier render a disabled-honest Pass.
 */
function PassControl({
  edge,
  name,
  onRoster,
  pass,
}: {
  edge: ConsultantRelationshipEdge;
  name: string | null;
  onRoster: boolean;
  pass?: RelationshipPassAction;
}): JSX.Element {
  const label = name ? `Pass signal to ${firstName(name)}` : "Pass signal";

  if (pass && name && onRoster) {
    return (
      <button
        type="button"
        className="sx-btn sx-btn-p3"
        disabled={
          pass.pendingOwnerId !== null ||
          pass.passedOwnerId === edge.ownerConsultantId
        }
        onClick={() => pass.onPass(edge.ownerConsultantId)}
      >
        {pass.pendingOwnerId === edge.ownerConsultantId
          ? "Passing…"
          : pass.passedOwnerId === edge.ownerConsultantId
            ? "Passed ✓"
            : pass.errorOwnerId === edge.ownerConsultantId
              ? "Couldn't pass — try again"
              : label}
      </button>
    );
  }

  const title = pass
    ? name
      ? "Not an active BDSE user"
      : "Owner isn't a resolved BDSE user"
    : "Coming soon";
  return (
    <button type="button" className="sx-btn sx-btn-p3" disabled title={title}>
      {label}
    </button>
  );
}
